import ThermostatIcon from '@mui/icons-material/Thermostat';
import { Box, SvgIcon, Typography } from '@mui/material';
import { AngleUnits } from 'convert-units/definitions/angle';
import { isAfter, isBefore, subMinutes } from 'date-fns';
import { compact, isString, orderBy } from 'lodash';
import { ReactNode, useMemo, useRef, useState } from 'react';

import { SensorAlertTypes } from '@/api/customSensorAlerts/types';
import { SensorType } from '@/api/device';
import { Device, useDeviceSystemErrors } from '@/api/devices';
import { CURRENTS_DIRECTION_DATA_KEY, ConsolidatedSensorDatum, useHistoricalSensorData } from '@/api/historicalData';
import { currentIcon, dissolvedOxygenIcon, hydrophoneIcon, pressureIcon, salinityIcon, turbidityIcon } from '@/assets';
import { AlertChip } from '@/components/AlertChip';
import { Datum } from '@/components/data/Datum';
import { DirectionDatum } from '@/components/data/DirectionDatum';
import { IconData, LabeledData } from '@/components/data/IconData';
import { LoadingIndicatorIcon } from '@/components/loaders/LoadingIndicatorIcon';
import { SensorAlert } from '@/components/map/SensorAlert';
import { MaybeTeleport } from '@/components/ui/MaybeTeleport';
import { SPL_READING_WINDOW_MINUTES } from '@/config';
import { useDateRangeContext } from '@/context/DateRangeContext';
import { useSensorPriorityContext } from '@/context/SensorPriorityContext';
import { useTimelineNavigationContext } from '@/context/TimelineNavigationContext';
import { useDateRangeForSample } from '@/hooks/useDateRangeForSample';
import { useIsInViewport } from '@/hooks/useIsInViewport';

export const SmartMooringDataReadouts = ({
    device,
    prioritySensorPortal,
    sampleTime = 'latest',
}: {
    device: Device;
    prioritySensorPortal?: Element | null;
    sampleTime?: 'latest' | 'timelineCursor';
}) => {
    const targetRef = useRef(null);
    const { prioritySensor } = useSensorPriorityContext();
    const isVisible = useIsInViewport(targetRef, { enabled: device?.isSmartMooring, elapsedMs: 100 });
    const { timeLastUpdatedUTC, lastMessageTime } = device;
    const wavesTimestamp = device.waves?.timestamp;
    // useState, not useMemo: a memo is a hint React may drop, and dropping this one would shift the query key
    const [fallbackTimestampMs] = useState(() => Date.now());
    const { selectedTimestamp } = useTimelineNavigationContext();
    const { isTrackingLive } = useDateRangeContext();
    const effectiveSampleTime = isTrackingLive || !selectedTimestamp ? 'latest' : sampleTime;
    const isTimelineCursorSample = effectiveSampleTime === 'timelineCursor';
    const dateRange = useDateRangeForSample(
        isTimelineCursorSample
            ? selectedTimestamp
            : (timeLastUpdatedUTC ?? wavesTimestamp ?? lastMessageTime ?? fallbackTimestampMs),
        // if we're looking for a specific time, look within a sample. if we're looking for "latest", cast a wider net (hopefully SM data came within 3 hours of the last message)
        // TODO https://app.shortcut.com/sofar/story/284300/clean-up-sampling-windows-post-bluetide
        isTimelineCursorSample ? SPL_READING_WINDOW_MINUTES : 180
    );
    const sampleWindowStart =
        isTimelineCursorSample && selectedTimestamp ? subMinutes(selectedTimestamp, SPL_READING_WINDOW_MINUTES) : null;

    const {
        data: sensorData,
        isLoading,
        isError,
    } = useHistoricalSensorData({
        spotterId: device.spotterId,
        dateRange,
        includeSoundPressureReadings: prioritySensor === 'hydrophone',
        excludeSoundPressureBandReadings: true,
        config: {
            enabled: device?.isSmartMooring && isVisible,
        },
    });

    const { data: systemErrors } = useDeviceSystemErrors({ spotterId: device.spotterId });
    const bridgeError = systemErrors?.bridge;

    const dataPerDataType: { dataKey: string; datum: ConsolidatedSensorDatum }[][] = useMemo(() => {
        if (!sensorData) {
            return [];
        }

        const sortedSensorData = orderBy(
            Object.values(sensorData).map((data) => orderBy(data, 'timestamp', 'desc')),
            (positionData) => positionData[0]?.sensorPosition ?? Infinity
        );

        return compact(
            SENSOR_READOUTS.map(({ keys }) => {
                // find the datum that is most recent (with sampleTime=latest) or last before selectedTime, with one of these keys in each sensor location, sorted by node number
                const dataPerKey: { dataKey: string; datum: ConsolidatedSensorDatum }[] = compact(
                    sortedSensorData.map((positionData) => {
                        for (const dataKey of keys) {
                            for (const datum of positionData) {
                                if (
                                    Number.isFinite(datum.data[dataKey]?.value) &&
                                    (effectiveSampleTime === 'latest' ||
                                        (!isBefore(datum.timestamp, sampleWindowStart!) &&
                                            !isAfter(datum.timestamp, selectedTimestamp!)))
                                ) {
                                    return { dataKey, datum };
                                }
                            }
                        }
                        return null;
                    })
                );
                if (!dataPerKey.length) return null;
                return dataPerKey;
            })
        );
    }, [effectiveSampleTime, sampleWindowStart, selectedTimestamp, sensorData]);

    if (!device?.isSmartMooring) {
        return false;
    }

    return (
        <>
            <Box ref={targetRef} sx={{ position: 'absolute', opacity: 0 }}>
                {/* Invisible sentinel that tracks whether the card is visible so we only load SM data when on
                    screen. position:absolute keeps it out of the flex flow. It must be the SOLE holder of
                    targetRef: this Box never unmounts, so React only sets its ref once. If the readouts below
                    also took targetRef, unmounting them on a query-key change would run their ref cleanup and
                    null out targetRef.current (the persistent Box's ref is never re-applied), so the visibility
                    check would read null, flip isVisible to false, and leave the query disabled forever. */}
            </Box>
            <LabeledData label="Smart Mooring" sx={{ display: isLoading || isError ? 'initial' : 'none' }}>
                <LoadingIndicatorIcon isLoading={isLoading} />
                {isError && <Typography>Error loading sensor data</Typography>}
            </LabeledData>
            {dataPerDataType.flatMap((data) =>
                data.map(({ dataKey, datum }) => {
                    const { Readout, sensorType } = SENSOR_KEY_TO_READOUT[dataKey];
                    return (
                        <MaybeTeleport
                            key={`${dataKey}-${datum.timestamp}-${datum.sensorPosition}`}
                            portal={prioritySensorPortal}
                            teleport={sensorType === prioritySensor}
                        >
                            <span>
                                <Readout
                                    device={device}
                                    sensorDatum={datum}
                                    dataKey={dataKey}
                                    showSensorPosition={data.length > 1}
                                />
                                {bridgeError && <AlertChip severity="error" label={bridgeError.description} />}
                            </span>
                        </MaybeTeleport>
                    );
                })
            )}
        </>
    );
};

type SensorReadoutProps = {
    device: Device;
    sensorDatum: ConsolidatedSensorDatum;
    dataKey: string;
    showSensorPosition?: boolean;
};

type SensorReadout = (props: SensorReadoutProps) => ReactNode;

const readoutLabel = (label: string, { sensorPosition }: { sensorPosition: number }, showSensorPosition?: boolean) =>
    showSensorPosition ? `${label} (${sensorPosition})` : label;

const scalarSensorReadout = (iconOrSrc: typeof SvgIcon | string, label: string, alertType?: SensorAlertTypes) =>
    function ScalarSensorReadout({ sensorDatum, dataKey, showSensorPosition, device }: SensorReadoutProps) {
        const { value, units, unitType } = sensorDatum.data[dataKey] ?? {};
        return (
            <IconData
                {...(isString(iconOrSrc) ? { src: iconOrSrc } : { Icon: iconOrSrc })}
                label={readoutLabel(label, sensorDatum, showSensorPosition)}
                timestamp={sensorDatum.timestamp}
            >
                <Datum value={value} sourceUnit={units} type={unitType} />
                {!!alertType && (
                    <SensorAlert
                        isSmartMooring
                        sensorPosition={sensorDatum.sensorPosition}
                        spotterId={device.spotterId}
                        alertType={alertType}
                        currentValue={value}
                        currentValueUnit={units}
                    />
                )}
            </IconData>
        );
    };

const WaterPressureSensorReadout = (alertType: SensorAlertTypes) =>
    function WaterPressureReadout({ sensorDatum, dataKey, device, showSensorPosition }: SensorReadoutProps) {
        const { value, units, unitType } = sensorDatum.data[dataKey] ?? {};
        return (
            <IconData
                src={pressureIcon}
                label={readoutLabel('Water pressure', sensorDatum, showSensorPosition)}
                timestamp={sensorDatum.timestamp}
            >
                <Datum
                    value={value}
                    sourceUnit={units}
                    type={unitType}
                    context={{ deploymentConfig: device.deploymentConfig }}
                />
                <SensorAlert
                    isSmartMooring
                    sensorPosition={sensorDatum.sensorPosition}
                    spotterId={device.spotterId}
                    alertType={alertType}
                    currentValue={value}
                    currentValueUnit={units}
                    context={{ deploymentConfig: device.deploymentConfig }}
                />
            </IconData>
        );
    };

const CurrentSensorReadout = (alertType: SensorAlertTypes) =>
    function CurrentReadout({ sensorDatum, dataKey, showSensorPosition, device }: SensorReadoutProps) {
        const { value: direction, units: directionUnit } = sensorDatum.data[CURRENTS_DIRECTION_DATA_KEY] ?? {};
        const { value, units } = sensorDatum.data[dataKey] ?? {};
        return (
            <IconData
                src={currentIcon}
                label={readoutLabel('Current', sensorDatum, showSensorPosition)}
                timestamp={sensorDatum.timestamp}
            >
                <Datum value={value} sourceUnit={units} type="currentSpeed" />
                <DirectionDatum type="flow" heading={direction} unit={directionUnit as AngleUnits} />
                <SensorAlert
                    isSmartMooring
                    sensorPosition={sensorDatum.sensorPosition}
                    spotterId={device.spotterId}
                    alertType={alertType}
                    currentValue={value}
                    currentValueUnit={units}
                />
            </IconData>
        );
    };

/*
 * They'll appear in the card in the order they're defined here.
 */
const SENSOR_READOUTS: { keys: string[]; readout: SensorReadout; sensorType: SensorType }[] = [
    {
        sensorType: 'waterTemperature',
        keys: [
            'sofar_temperature_12bits',
            'bm_soft_temperature_mean_13bits',
            'bm_rbr_coda_temperature_mean_20bits',
            'rbrcoda3_meantemperature_20bits',
            'aanderaa_temperature_mean_13bits',
            'bm_do_temperature_mean_16bits',
            'bm_aanderaa_salinitysensor_temperature_mean_16bits',
        ],
        readout: scalarSensorReadout(ThermostatIcon, 'Water temperature', 'smartmooring_threshold_temperature'),
    },
    {
        sensorType: 'hydrophone',
        // it is significant that _reading comes first; it means we'll favor it if we find both among the data
        keys: [
            'bm_borealis_spl_broadband_reading',
            'bm_borealis2_spl_broadband_reading',
            'bm_borealis_spl_broadband_8bits',
            'bm_borealis2_spl_broadband_8bits',
        ],
        readout: scalarSensorReadout(hydrophoneIcon, 'Sound pressure', 'smartmooring_threshold_broadband_spl'),
    },
    {
        sensorType: 'dissolvedOxygen',
        keys: ['bm_do_mg_per_l_mean_17bits'],
        readout: scalarSensorReadout(
            dissolvedOxygenIcon,
            'Dissolved oxygen',
            'smartmooring_threshold_dissolved_oxygen_concentration'
        ),
    },
    {
        sensorType: 'salinity',
        keys: ['bm_aanderaa_salinity_mean_16bits'],
        readout: scalarSensorReadout(salinityIcon, 'Salinity', 'smartmooring_threshold_salinity'),
    },
    {
        sensorType: 'currents',
        keys: ['aanderaa_abs_speed_mean_15bits'],
        readout: CurrentSensorReadout('smartmooring_threshold_currents_speed'),
    },
    {
        sensorType: 'turbidity',
        keys: ['seapoint_meanturbidity_24bits', 'bm_seapoint_turbidity_s_mean_21bits'],
        readout: scalarSensorReadout(turbidityIcon, 'Turbidity', 'smartmooring_threshold_turbidity'),
    },
    {
        sensorType: 'pressure',
        keys: ['bm_rbr_coda_pressure_mean_21bits', 'rbrcoda3_meanpressure_21bits'],
        readout: WaterPressureSensorReadout('smartmooring_threshold_water_pressure'),
    },
];

const SENSOR_KEY_TO_READOUT: { [key: string]: { Readout: SensorReadout; sensorType: SensorType } } = Object.fromEntries(
    SENSOR_READOUTS.flatMap(({ keys, readout, sensorType }) => keys.map((k) => [k, { Readout: readout, sensorType }]))
);
